import React, { useState, useEffect } from "react";
import checkPermission from '../Utils/checkPermission';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Switch,
  FormControlLabel,
  Button,
  MenuItem,
  Typography,
} from "@mui/material";
import axiosApi from "../Utils/axiosApi";

const HolidayFormDialog = ({ open, onClose, holiday, hostelId, onSave }) => {
  const [formData, setFormData] = useState({
    id: "",
    hostelId: "",
    scheduleType: "",
    startDate: "",
    endDate: "",
    reason: "",
    stayClosed: "N",
    foodNotAvailable: "N",
    remarks: "",
  });

  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");

  // ✅ Reset form when props change
  useEffect(() => {
    if (holiday) {
      setFormData({
        id: holiday.id || "",
        hostelId: holiday.hostelId || hostelId || "",
        scheduleType: holiday.scheduleType || "",
        startDate: holiday.startDate ? holiday.startDate.split("T")[0] : "",
        endDate: holiday.endDate ? holiday.endDate.split("T")[0] : "",
        reason: holiday.reason || "",
        stayClosed: holiday.stayClosed === "true" ? "Y" : "N",
        foodNotAvailable: holiday.foodClosed === "true" ? "Y" : "N", // map API → form
        remarks: holiday.remarks || "",

      });
    } else {
      // reset for add mode
      setFormData({
        id: "",
        hostelId: hostelId || "",
        scheduleType: "",
        startDate: "",
        endDate: "",
        reason: "",
        stayClosed: "N",
        foodNotAvailable: "N",
        remarks: "",
      });
    }

    setErrorMessage("");
    setSuccessMessage("");
  }, [holiday, hostelId]);

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async () => {
    // ✅ Transform before sending to backend
    const payloadData = {
      ...formData,
      hostelId,
      stayClosed: formData.stayClosed === "Y" ? "true" : "false",
      foodClosed: formData.foodNotAvailable === "Y" ? "true" : "false", // backend expects foodClosed
    };


    const { hostelId: hId, scheduleType, startDate, endDate } = payloadData;

    // ✅ Validation
    if (!hId || !scheduleType || !startDate || !endDate) {
      setErrorMessage("⚠️ Please fill all mandatory fields");
      return;
    }

    if (new Date(startDate) > new Date(endDate)) {
      setErrorMessage("⚠️ End Date cannot be before Start Date.");
      return;
    }

    try {
      let response;

      if (formData.id) {
        // ✅ Update API
        response = await axiosApi.post(
          "/hostelHolidayOrClosure/updateHostelHolidayOrClosure",
          { hostelHolidayOrClosureDTO: payloadData }
        );
      } else {
        // ✅ Create API
        response = await axiosApi.post(
          "/hostelHolidayOrClosure/createHostelHolidayOrClosure",
         { hostelHolidayOrClosureDTO: payloadData }
        );
      }

      if (response.status === 200 || response.status === 201) {
        setSuccessMessage("✅ Holiday/Closure saved successfully");
        if (onSave) onSave(payloadData);
        //onClose();
      } else {
        setErrorMessage("❌ Failed to save Holiday/Closure");
      }
    } catch (error) {
      console.error("API Error:", error);
      setErrorMessage(
        error.response?.data?.message || "❌ Something went wrong while saving."
      );
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>
        {formData.id ? "Edit Holiday / Closure" : "Add Holiday / Closure"}
      </DialogTitle>

      <DialogContent dividers>
        {/* Schedule Type */}
        <TextField
          margin="dense"
          label="Schedule Type"
          select
          fullWidth
          required
          value={formData.scheduleType}
          onChange={(e) => handleChange("scheduleType", e.target.value)}
        >
          <MenuItem value="HOLIDAY">Holiday</MenuItem>
          <MenuItem value="CLOSURE">Closure</MenuItem>
        </TextField>

        {/* Start Date */}
        <TextField
          margin="dense"
          type="date"
          label="Start Date"
          InputLabelProps={{ shrink: true }}
          fullWidth
          required
          value={formData.startDate}
          onChange={(e) => handleChange("startDate", e.target.value)}
        />

        {/* End Date */}
        <TextField
          margin="dense"
          type="date"
          label="End Date"
          InputLabelProps={{ shrink: true }}
          fullWidth
          required
          value={formData.endDate}
          inputProps={{ min: formData.startDate }} // prevent selecting before start
          onChange={(e) => handleChange("endDate", e.target.value)}
        />

        {/* Switches */}
        <FormControlLabel
          control={
            <Switch
              checked={formData.stayClosed === "Y"}
              onChange={(e) =>
                handleChange("stayClosed", e.target.checked ? "Y" : "N")
              }
            />
          }
          label="Stay Closed"
        />

        <FormControlLabel
          control={
            <Switch
              checked={formData.foodNotAvailable === "Y"}
              onChange={(e) =>
                handleChange("foodNotAvailable", e.target.checked ? "Y" : "N")
              }
            />
          }
          label="No Food Available"
        />

        {/* Reason */}
        <TextField
          margin="dense"
          label="Reason"
          fullWidth
          value={formData.reason}
          onChange={(e) => handleChange("reason", e.target.value)}
        />

        {/* Remarks */}
        <TextField
          margin="dense"
          label="Remarks"
          fullWidth
          multiline
          rows={2}
          value={formData.remarks}
          onChange={(e) => handleChange("remarks", e.target.value)}
        />

        {/* Inline error/success messages */}
        {errorMessage && (
          <Typography color="error" variant="body2" sx={{ mt: 1 }}>
            {errorMessage}
          </Typography>
        )}
        {successMessage && (
          <Typography color="success.main" variant="body2" sx={{ mt: 1 }}>
            {successMessage}
          </Typography>
        )}
      </DialogContent>

      <DialogActions>
        <Button  onClick={onClose}>Cancel</Button>
        <Button disabled={!checkPermission("Hostel", "Hostel List", "UPDATEHOLIDAYDETAIL")} variant="contained" onClick={handleSubmit}>
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default HolidayFormDialog;
